import { useEffect, useState } from "react";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import { useSettings, useServicesLabel } from "../context/SettingsContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Settings as SettingsIcon, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const empty = { name: "", default_cost: 0, notes: "", linked_parts: [] };

export default function Services() {
  const { settings, update: updateSettings } = useSettings();
  const svcLabel = useServicesLabel();
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const [labelOpen, setLabelOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([api.get("/services"), api.get("/parts")]);
      setItems(s.data);
      setParts(p.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    const lp = (s.linked_parts || []).map((p, i) => ({ ...p, _uid: p._uid || `lp-${s.id}-${i}` }));
    setForm({ ...empty, ...s, linked_parts: lp });
    setOpen(true);
  };

  const addLinkedPart = () => {
    const uid = `lp-new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setForm({ ...form, linked_parts: [...form.linked_parts, { _uid: uid, part_id: "", quantity: 1 }] });
  };
  const updateLinkedPart = (idx, patch) => {
    const next = form.linked_parts.map((lp, i) => (i === idx ? { ...lp, ...patch } : lp));
    setForm({ ...form, linked_parts: next });
  };
  const removeLinkedPart = (idx) => {
    setForm({ ...form, linked_parts: form.linked_parts.filter((_, i) => i !== idx) });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const linked = (form.linked_parts || [])
        .filter((lp) => lp.part_id && Number(lp.quantity) > 0)
        .map((lp) => ({ part_id: lp.part_id, quantity: Number(lp.quantity) }));
      const payload = {
        name: form.name,
        default_cost: Number(form.default_cost),
        notes: form.notes || "",
        linked_parts: linked,
      };
      if (editing) {
        await api.patch(`/services/${editing.id}`, payload);
        toast.success(`${svcLabel.singular} updated`);
      } else {
        await api.post("/services", payload);
        toast.success(`${svcLabel.singular} added`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onSaveLabel = async (e) => {
    e.preventDefault();
    const v = labelDraft.trim();
    if (!v) return toast.error("Label cannot be empty");
    try {
      await updateSettings({ services_label: v });
      toast.success("Label updated");
      setLabelOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-6xl">
      <PageHeader
        eyebrow="Catalog"
        title={settings.services_label}
        description={`Document recurring ${svcLabel.pluralLower} and their default cost. Use them when adding expenses to a vehicle.`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setLabelDraft(settings.services_label); setLabelOpen(true); }}
              data-testid="rename-section-btn"
              className="rounded-sm border-zinc-300"
            >
              <SettingsIcon className="w-4 h-4 mr-2" /> Rename section
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="add-service-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  <Plus className="w-4 h-4 mr-2" /> Add {settings.services_label.replace(/s$/i, "")}
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl tracking-tight">
                    {editing ? "Edit entry" : "New entry"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSave} className="space-y-4" data-testid="service-form">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                    <Input required placeholder="e.g. Oil change" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" className="rounded-sm border-zinc-300" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Default Cost</Label>
                    <Input required type="number" min={0} step="0.01" value={form.default_cost} onChange={(e) => setForm({ ...form, default_cost: e.target.value })} data-testid="service-cost-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={2} />
                  </div>
                  <div className="pt-2 border-t border-zinc-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Auto-deduct parts</Label>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          When this is used as a vehicle expense, these parts are auto-deducted from inventory. Out-of-stock items are skipped.
                        </div>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={addLinkedPart} data-testid="add-linked-part" className="rounded-sm border-zinc-300">
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {(form.linked_parts || []).length === 0 && (
                      <div className="text-xs text-zinc-400 italic">No linked parts.</div>
                    )}
                    <div className="space-y-2">
                      {(form.linked_parts || []).map((lp, idx) => (
                        <div key={lp._uid || `lp-${idx}`} className="flex items-center gap-2">
                          <Select value={lp.part_id || ""} onValueChange={(v) => updateLinkedPart(idx, { part_id: v })}>
                            <SelectTrigger className="rounded-sm border-zinc-300 flex-1" data-testid={`linked-part-${idx}`}>
                              <SelectValue placeholder="Select part..." />
                            </SelectTrigger>
                            <SelectContent>
                              {parts.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} ({p.sku})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min={1}
                            value={lp.quantity}
                            onChange={(e) => updateLinkedPart(idx, { quantity: e.target.value })}
                            data-testid={`linked-qty-${idx}`}
                            className="w-20 rounded-sm border-zinc-300 font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => removeLinkedPart(idx)}
                            data-testid={`remove-linked-${idx}`}
                            className="text-zinc-400 hover:text-[#FF2400] p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                    <Button type="submit" disabled={saving} data-testid="service-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                      {saving ? "Saving..." : editing ? "Save changes" : "Add"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Rename this section</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSaveLabel} className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Section label</Label>
              <Input required value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)} data-testid="label-input" className="rounded-sm border-zinc-300" />
              <div className="text-xs text-zinc-500 mt-1">Shown in the sidebar and as this page's title.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLabelOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" data-testid="label-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Name", "Default Cost", "Auto-deduct", "Notes", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (<tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">Loading...</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-500">No {svcLabel.pluralLower} yet. Click "Add {svcLabel.singular}" to start.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="hover:bg-zinc-50" data-testid={`service-row-${s.id}`}>
                <td className="px-4 py-3 text-sm font-semibold text-zinc-950">{s.name}</td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-900">{currency(s.default_cost)}</td>
                <td className="px-4 py-3 text-xs text-zinc-600">
                  {(s.linked_parts || []).length === 0 ? (
                    <span className="text-zinc-300">—</span>
                  ) : (
                    <span data-testid={`linked-count-${s.id}`} className="font-mono">
                      {s.linked_parts.length} part{s.linked_parts.length > 1 ? "s" : ""}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-700 truncate max-w-xs">{s.notes || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`} className="text-zinc-500 hover:text-zinc-950 mr-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(s)} data-testid={`delete-service-${s.id}`} className="text-zinc-500 hover:text-[#FF2400]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
